import React, { FunctionComponent } from 'react';

import SpotMarker from 'components/SpotMarker';
import SpotMarkerTooltip from 'components/SpotMarkerTooltip';
import type { Camera } from 'types/camera';
import type { UserPermissions } from 'types/permissions';
import type { Units } from 'types/units';
import { showPremiumCamCTA } from 'utils/premiumCam';

import styles from '../SpotMap.module.scss';

interface SpotData {
  _id: string;
  lat: number;
  lon: number;
  waveHeight: {
    min: number;
    max: number;
    plus?: boolean;
  };
  conditions: {
    value: string;
  };
  cameras: Camera[];
  zone?: {
    title: string;
  };
  title?: string;
}

interface AnimationsState {
  activeSpotId: string | null;
}

interface MarkerPosition {
  vertical: 'top' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
}

interface SpotMarkerOverlayProps {
  spot: SpotData;
  style: React.CSSProperties;
  units: Units;
  animations: AnimationsState;
  permissions: UserPermissions;
  tooltipPosition: MarkerPosition;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
  currentSpotId: string | null;
}

/**
 * Spot marker with optional tooltip
 */
const SpotMarkerOverlay: FunctionComponent<SpotMarkerOverlayProps> = ({
  spot,
  style,
  units,
  animations,
  permissions,
  tooltipPosition,
  onMouseEnter,
  onMouseLeave,
  onClick,
  currentSpotId,
}) => {
  const isActive = animations.activeSpotId === spot._id;
  const isCurrent = currentSpotId === spot._id;

  return (
    <div
      className={styles.spotMapOverlayMarker}
      style={style}
      key={`${spot.lat},${spot.lon}`}
      data-testid="spot-marker"
    >
      <SpotMarker
        surf={{
          max: spot.waveHeight.max,
          min: spot.waveHeight.min,
          plus: spot.waveHeight.plus,
          units: units.waveHeight,
        }}
        conditions={spot.conditions.value || 'LOTUS'}
        hasCamera={spot.cameras.length > 0}
        highlight={isActive || isCurrent}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onClick={onClick}
        current={isCurrent}
      />
      {isActive ? (
        <SpotMarkerTooltip
          spot={spot}
          units={units}
          position={tooltipPosition}
          showPremiumCamCTA={showPremiumCamCTA({
            spot,
            hasPremiumCamPermissions: permissions.hasPremiumCamPermissions || false,
          })}
        />
      ) : null}
    </div>
  );
};

export default SpotMarkerOverlay;
